// Pure clause-emission helpers for the per-CRM filter section (CP-7).
//
// The section's two new controls — the account-status MULTI-select and the
// last-activity selector — both write EXCLUDE-polarity ``CrmFilterClause``s
// whose ``condition`` is the frozen /crm/exclusion-set token. The token
// grammar lives HERE (pure, React/RTK-free) so the mapping is unit-testable in
// isolation and can't drift between the UI and the chip labeller.
//
// Tokens (all EXCLUDE-only this PR):
//   account_status:<value>                  legacy default-field selection
//   account_status:@<field>:<value>         explicit picklist field
//   last_activity_over:<days>[:<scope>]      ← dormant (no activity in N days)
//   last_activity_within:<days>[:<scope>]    ← active  (activity within N days)
//   scope ∈ accounts | contacts | both       (default both → omitted)
//   crm_accounts                             every account in the CRM
//   field:<object>.<Field>:<op>[:<value>]    any CRM field (value may hold ':')
//   object ∈ contact | lead | account        op ∈ eq ne contains gt lt empty not_empty

import type { CrmFilterClause } from '@/types/search-filters';
import type {
  CrmAccountStatusCondition,
  CrmFieldCondition,
  CrmFieldObject,
  CrmFieldOp,
  CrmLastActivityCondition,
  CrmLastActivityScope,
} from '@/store/features/crm.feature';

export const ACCOUNT_STATUS_PREFIX = 'account_status:';
export const LAST_ACTIVITY_OVER_PREFIX = 'last_activity_over:';
export const LAST_ACTIVITY_WITHIN_PREFIX = 'last_activity_within:';
export const FIELD_PREFIX = 'field:';
/** Every account in the CRM; the "not my CRM accounts" exclude. */
export const CRM_ACCOUNTS_CONDITION = 'crm_accounts';

export const CRM_FIELD_OBJECTS: readonly CrmFieldObject[] = [
  'contact',
  'lead',
  'account',
];
export const CRM_FIELD_OPS: readonly CrmFieldOp[] = [
  'eq',
  'ne',
  'contains',
  'gt',
  'lt',
  'empty',
  'not_empty',
];
const FIELD_OPS_WITHOUT_VALUE: readonly CrmFieldOp[] = ['empty', 'not_empty'];

/** Each operator in words, for the builder's operator select and the chips. */
export const CRM_FIELD_OP_LABEL: Record<CrmFieldOp, string> = {
  eq: 'is',
  ne: 'is not',
  contains: 'contains',
  gt: '>',
  lt: '<',
  empty: 'is empty',
  not_empty: 'is not empty',
};

/** A clause a search can carry: a nonblank id and condition, and an
 *  include/exclude polarity. The one definition the saved-search migrator and
 *  the People and company validators share. */
export function isWellFormedCrmClause(
  clause: unknown
): clause is CrmFilterClause {
  if (!clause || typeof clause !== 'object') return false;
  const { id, polarity, condition } = clause as Record<string, unknown>;
  return (
    typeof id === 'string' &&
    id.trim() !== '' &&
    typeof condition === 'string' &&
    condition.trim() !== '' &&
    (polarity === 'include' || polarity === 'exclude')
  );
}

/** True for the operators that carry a value (everything but empty/not_empty). */
export function fieldOpTakesValue(op: CrmFieldOp): boolean {
  return !FIELD_OPS_WITHOUT_VALUE.includes(op);
}

/** Last-activity mode: dormant (``over``) vs active (``within``). */
export type LastActivityMode = 'dormant' | 'active';

/** Resolution scope for the last-activity condition. ``both`` is the BE
 *  default and is omitted from the token so the common case stays compact. */
export type LastActivityScope = CrmLastActivityScope;

/** Build the EXCLUDE account-status condition token for one
 *  selected picklist value. Returns null when the value is blank (nothing to
 *  emit). The value is NOT url-encoded — the BE matches the raw picklist
 *  value, and the exclusion-set contract templates it verbatim. */
export function accountStatusCondition(
  value: string,
  field?: string
): CrmAccountStatusCondition | null {
  const v = value.trim();
  if (!v) return null;
  if (field) return `account_status:@${field}:${v}`;
  return `account_status:${v}`;
}

/** Extract the selected picklist value from an ``account_status:<value>``
 *  token, or null when the token isn't an account-status condition. */
export function parseAccountStatusValue(condition: string): string | null {
  if (!condition.startsWith(ACCOUNT_STATUS_PREFIX)) return null;
  const tail = condition.slice(ACCOUNT_STATUS_PREFIX.length);
  const field = parseAccountStatusField(condition);
  const value = field ? tail.slice(field.length + 2) : tail;
  return value || null;
}

/** Explicit marker keeps legacy status values containing colons intact. */
export function parseAccountStatusField(condition: string): string | undefined {
  if (!condition.startsWith(ACCOUNT_STATUS_PREFIX)) return undefined;
  return /^@([A-Za-z][A-Za-z0-9_]*):/.exec(
    condition.slice(ACCOUNT_STATUS_PREFIX.length)
  )?.[1];
}

/** Build the EXCLUDE last-activity condition token.
 *  - ``dormant`` → ``last_activity_over:<days>``  (no activity in N days,
 *    includes never-active records — the BE's over-set semantics).
 *  - ``active``  → ``last_activity_within:<days>`` (activity within N days).
 *  A non-``both`` scope is appended as ``:<scope>``; ``both`` is omitted.
 *  Returns null when ``days`` is not a positive finite integer (nothing to
 *  emit / clears the control). */
export function lastActivityCondition(
  mode: LastActivityMode,
  days: number,
  scope: LastActivityScope = 'both'
): CrmLastActivityCondition | null {
  if (!Number.isFinite(days) || days <= 0) return null;
  const d = Math.floor(days);
  if (mode === 'dormant') {
    return scope === 'both'
      ? `last_activity_over:${d}`
      : `last_activity_over:${d}:${scope}`;
  }
  return scope === 'both'
    ? `last_activity_within:${d}`
    : `last_activity_within:${d}:${scope}`;
}

export interface ParsedLastActivity {
  mode: LastActivityMode;
  days: number;
  scope: LastActivityScope;
}

/** Parse a last-activity token back into its mode / days / scope, or null
 *  when ``condition`` isn't a last-activity token (or is malformed). Used by
 *  the section to hydrate the control from the existing clause. */
export function parseLastActivityCondition(
  condition: string
): ParsedLastActivity | null {
  let mode: LastActivityMode;
  let tail: string;
  if (condition.startsWith(LAST_ACTIVITY_OVER_PREFIX)) {
    mode = 'dormant';
    tail = condition.slice(LAST_ACTIVITY_OVER_PREFIX.length);
  } else if (condition.startsWith(LAST_ACTIVITY_WITHIN_PREFIX)) {
    mode = 'active';
    tail = condition.slice(LAST_ACTIVITY_WITHIN_PREFIX.length);
  } else {
    return null;
  }
  const [daysRaw, scopeRaw] = tail.split(':');
  const days = Number(daysRaw);
  if (!Number.isFinite(days) || days <= 0) return null;
  const scope: LastActivityScope =
    scopeRaw === 'accounts' || scopeRaw === 'contacts' ? scopeRaw : 'both';
  return { mode, days: Math.floor(days), scope };
}

/** True when ``condition`` is either last-activity token. The section keeps at
 *  most one last-activity clause, so this is the predicate used to strip the
 *  existing one before writing a replacement. */
export function isLastActivityCondition(condition: string): boolean {
  return (
    condition.startsWith(LAST_ACTIVITY_OVER_PREFIX) ||
    condition.startsWith(LAST_ACTIVITY_WITHIN_PREFIX)
  );
}

/** Build the EXCLUDE ``field:`` condition for one CRM field, or null when the
 *  parts cannot make one: an unknown object or operator, a blank field name, a
 *  field name holding a colon or whitespace, or a value-taking operator with a
 *  blank value. ``empty`` / ``not_empty`` carry no value. The value is kept
 *  verbatim and may itself contain colons, because the server reads everything
 *  after the third colon as the value. */
export function fieldCondition(
  object: CrmFieldObject,
  field: string,
  op: CrmFieldOp,
  value?: string
): CrmFieldCondition | null {
  if (!CRM_FIELD_OBJECTS.includes(object)) return null;
  if (!CRM_FIELD_OPS.includes(op)) return null;
  const name = field.trim();
  if (!name || /[:\s]/.test(name)) return null;
  if (!fieldOpTakesValue(op)) return `field:${object}.${name}:${op}`;
  if (value == null || value.trim() === '') return null;
  return `field:${object}.${name}:${op}:${value}`;
}

export interface ParsedFieldCondition {
  object: CrmFieldObject;
  field: string;
  op: CrmFieldOp;
  /** Null for ``empty`` / ``not_empty``. */
  value: string | null;
}

const FIELD_CONDITION_PATTERN =
  /^field:(contact|lead|account|company)\.([^:\s]+):(eq|ne|contains|gt|lt|empty|not_empty)(?::([\s\S]*))?$/;

/** Parse a ``field:`` token back into its parts, or null when ``condition``
 *  is not a well-formed one (another kind of token, a missing part, an
 *  unknown operator, an empty value, or a value on ``empty`` / ``not_empty``).
 *  ``company`` is read as ``account``, as the server does. */
export function parseFieldCondition(
  condition: string
): ParsedFieldCondition | null {
  const match = FIELD_CONDITION_PATTERN.exec(condition);
  if (!match) return null;
  const [, rawObject, field, rawOp, value] = match;
  const op = rawOp as CrmFieldOp;
  const object = (
    rawObject === 'company' ? 'account' : rawObject
  ) as CrmFieldObject;
  if (!fieldOpTakesValue(op)) {
    return value === undefined ? { object, field, op, value: null } : null;
  }
  return value ? { object, field, op, value } : null;
}

/** Key of a field in a label map (``lead.Status``). */
export function fieldLabelKey(object: CrmFieldObject, field: string): string {
  return `${object}.${field}`;
}

/** True when ``condition`` would exclude people rather than accounts: the
 *  tokens a company search refuses. A ``field:`` token is judged by its object,
 *  and a last-activity token by an explicit ``contacts`` scope. */
export function conditionExcludesPeople(condition: string): boolean {
  const token = condition.trim();
  if (token === 'in_crm' || token === 'owned_any') return true;
  if (token.startsWith('owned_active_within:')) return true;
  if (token.startsWith(FIELD_PREFIX)) {
    const parsed = parseFieldCondition(token);
    if (parsed) return parsed.object !== 'account';
    return /^field:(contact|lead)\./.test(token);
  }
  return parseLastActivityCondition(token)?.scope === 'contacts';
}
